import React, { useCallback } from 'react';
import * as Yup from 'yup';
import _ from 'lodash';
import { connect, useSelector } from 'react-redux';
import Select from '../../../components/form-elements/Select';
import { carBrandsResponseSelector } from '../../../../modules/selectors/carBrands';
import { apiClient, useAsyncFunction } from '../../../../helpers/apiHelperV2';
import Input from '../../../components/form-elements/Input';
import ReduxSuspense from '../../../components/ReduxSuspense/ReduxSuspense';
import Mileage, { MileageInitialValues, MileageSchemaFields } from '../../../components/form-fragments/Mileage';
import VehicleIdentNumInfoDialog from '../../../components/VehicleIdentNumInfoDialog/VehicleIdentNumInfoDialog';
import { fetchCarBrandsIfNeeded } from '../../../../modules/actions/carBrands';
import { frontloadConnect } from 'react-frontload';
import { markenStandortSelector } from '../../../../modules/selectors/standort/markenStandort';

const REQUIRED_MESSAGE =
    'Bitte füllen Sie entweder die Fahrgestellnummer aus oder wählen Sie sowohl Ihre Fahrzeugmarke als auch Ihr Fahrzeugmodell.';

const validationSchema = Yup.object().shape(
    {
        vehicleIdentificationNumber: Yup.string().when(['carBrand', 'carModel'], {
            is: (carBrand, carModel) => !carBrand || !carModel,
            then: Yup.string().length(17, 'Die Fahrgestellnummer muss 17 Zeichen lang sein.').required(REQUIRED_MESSAGE),
        }),
        carBrand: Yup.string().when('vehicleIdentificationNumber', {
            is: vehicleIdentificationNumber => !vehicleIdentificationNumber,
            then: Yup.string().required(REQUIRED_MESSAGE),
        }),
        carModel: Yup.string().when('vehicleIdentificationNumber', {
            is: vehicleIdentificationNumber => !vehicleIdentificationNumber,
            then: Yup.string().required(REQUIRED_MESSAGE),
        }),
        ...MileageSchemaFields,
    },
    [
        // Define combinations that are not valid, additional to the straight forward ones defined above. (ALWAYS TUPLES!!!)
        ['vehicleIdentificationNumber', 'carBrand'],
        ['vehicleIdentificationNumber', 'carModel'],
    ]
);

function InactiveChildren({ values }) {
    return (
        <>
            {(values.vehicleIdentificationNumber || values.carBrand || values.carModel) && (
                <p className="uk-text-bold">Ihre Fahrzeugdaten:</p>
            )}
            {values.vehicleIdentificationNumber && (
                <dl className="uk-description-list">
                    <dt>Ihre Fahrgestellnummer:</dt>
                    <dd>{values.vehicleIdentificationNumber}</dd>
                </dl>
            )}
            {(values.carBrand || values.carModel) && (
                <dl className="uk-description-list">
                    <dt>Ihr gewähltes Fahrzeug:</dt>
                    <dd>
                        {values.carBrand} {values.carModel}
                    </dd>
                </dl>
            )}
            {values.mileage && (
                <dl className="uk-description-list">
                    <dt>Ihr Kilometerstand:</dt>
                    <dd>{values.mileage}</dd>
                </dl>
            )}
        </>
    );
}

function VehicleIdentificationNumberLabel() {
    return (
        <div className="uk-flex uk-flex-middle">
            Fahrgestellnummer /FIN(17-stellig) *<VehicleIdentNumInfoDialog />
        </div>
    );
}

function ActiveChildren({ values, setFieldValue, handleBlur, touched, errors, carBrandsResponse }) {
    const onChange = useCallback(({ name, value }) => setFieldValue(name, value), [setFieldValue]);
    const carModelsResponse = useAsyncFunction(async () => {
        return apiClient
            .get(`/api/v1/CarModelsForManufacturer`, { params: { manufacturer: values.carBrand } })
            .then(response => response.data?.data ?? []);
    }, [values.carBrand]);

    const markenStandort = useSelector(markenStandortSelector);

    const { implementsAreaConcept, areaShopSubLocations, hauptFabrikat, nebenFabrikat, url } = markenStandort;

    return (
        <>
            <div>Geben Sie Ihre Fahrzeugdaten an</div>
            <Input
                value={values.vehicleIdentificationNumber}
                onBlur={handleBlur}
                error={touched.vehicleIdentificationNumber && errors.vehicleIdentificationNumber}
                label={<VehicleIdentificationNumberLabel />}
                name="vehicleIdentificationNumber"
                id="vehicleIdentificationNumber"
                onChange={onChange}
                placeholder="Feld E in den Autopapieren"
                minLength={17}
                maxLength={17}
            />
            <div className="uk-margin">
                <div className="uk-text-center uk-text-bold">ODER</div>
            </div>
            <ReduxSuspense response={carBrandsResponse}>
                {carBrands => {
                    const carMakes =
                    (implementsAreaConcept && url === 'gottfried-schultz'
                        ? areaShopSubLocations && _.uniq([...areaShopSubLocations.map(loc => loc.hauptFabrikat), ...nebenFabrikat])
                        : carBrands.map(({marke}) => marke));

                    if (_.isEmpty(carBrands)) return null;
                    return (
                        <Select
                            value={values.carBrand}
                            onBlur={handleBlur}
                            error={touched.carBrand && errors.carBrand}
                            label="Ihre Fahrzeugmarke *"
                            name="carBrand"
                            id="carBrand"
                            onChange={onChange}
                            options={[
                                { label: 'Wählen Sie eine Fahrzeugmarke aus', value: '' },
                                ...carMakes.map((marke) => ({ label: marke, value: marke })),
                            ]}
                        />
                    );
                }}
            </ReduxSuspense>
            <ReduxSuspense response={carModelsResponse}>
                {carModels => {
                    if (_.isEmpty(carModels)) return null;
                    return (
                        <Select
                            onBlur={handleBlur}
                            error={touched.carModel && errors.carModel}
                            label="Ihr Fahrzeugmodell *"
                            name="carModel"
                            id="carModel"
                            value={values.carModel}
                            onChange={onChange}
                            options={[
                                { label: 'Wählen Sie ein Fahrzeugmodell aus', value: '' },
                                ...carModels.map(carModel => carModel.Value),
                            ]}
                        />
                    );
                }}
            </ReduxSuspense>
            <Mileage errors={errors} touched={touched} values={values} onChange={onChange} onBlur={handleBlur} />
        </>
    );
}

const frontload = async props => {
    await props.fetchCarBrandsIfNeeded();
};

const mapStateToProps = (state, props) => ({
    carBrandsResponse: carBrandsResponseSelector(state),
    ...props,
});

const mapDispatchToProps = { fetchCarBrandsIfNeeded };

const ConnectedInactiveChildren = connect(mapStateToProps, mapDispatchToProps)(frontloadConnect(frontload)(InactiveChildren));
const ConnectedActiveChildren = connect(mapStateToProps, mapDispatchToProps)(frontloadConnect(frontload)(ActiveChildren));

export const carSelectorStepFactory = () => {
    return {
        inactiveChildren: props => <ConnectedInactiveChildren {...props} />,
        activeChildren: props => <ConnectedActiveChildren {...props} />,
        title: 'Fahrzeug',
        initialValues: { carBrand: '', carModel: '', vehicleIdentificationNumber: '', ...MileageInitialValues },
        validationSchema,
    };
};
